import type { TenantUserActivationFragment } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.fragment"
import { waveWillEmail } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type {
  MigrationCenterSelection,
  MigrationCenterTenantDescriptor,
  MigrationCenterMemberSort,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.types"

export const NO_CUSTOMER_GROUP = "No customer"

/** Flattens any selection kind into the teams the board query loads. */
export function resolveTenantDescriptors(
  selection: MigrationCenterSelection,
): MigrationCenterTenantDescriptor[] {
  switch (selection.kind) {
    case "tenants":
      return selection.tenants.map((t) => ({
        tenantId: t.Id,
        tenantName: t.Name,
        customerName: t.CustomerName ?? NO_CUSTOMER_GROUP,
      }))
    case "customers":
      return selection.customers.flatMap((c) =>
        c.Tenants.map((ref) => ({
          tenantId: ref.Id,
          tenantName: ref.Name,
          customerName: c.Name ?? NO_CUSTOMER_GROUP,
        })),
      )
    case "users": {
      const byTenantId = new Map<number, MigrationCenterTenantDescriptor>()
      for (const user of selection.users) {
        for (const ref of user.Tenants) {
          byTenantId.set(ref.Id, {
            tenantId: ref.Id,
            tenantName: ref.Name,
            customerName: user.Customers[0]?.Name ?? NO_CUSTOMER_GROUP,
          })
        }
      }
      return [...byTenantId.values()]
    }
    case "tenantUsers":
      return [
        {
          tenantId: selection.tenant.Id,
          tenantName: selection.tenant.Name,
          customerName: selection.tenant.CustomerName ?? NO_CUSTOMER_GROUP,
        },
      ]
  }
}

/** Client-side rollup over board rows, for the big bar + group bars. */
export function rollupFromRows(rows: TenantUserActivationFragment[]) {
  const count = (predicate: (r: TenantUserActivationFragment) => boolean) =>
    rows.filter(predicate).length
  return {
    MigrationTotal: rows.length,
    // MIGRATED_ELSEWHERE also means "has a Fusion login" — done.
    MigrationActivated: count(
      (r) => r.Status === "ACTIVATED" || r.Status === "MIGRATED_ELSEWHERE",
    ),
    MigrationPending: count((r) => r.Status === "PENDING"),
    MigrationExpired: count((r) => r.Status === "EXPIRED"),
    MigrationBlocked: count((r) => r.Status === "BLOCKED"),
  }
}

// What the send buttons will actually email: first sends + expired-link
// renewals. Pending members keep their valid link (row send = explicit
// resend), so they don't count toward send numbers.
export function sendableRows(rows: TenantUserActivationFragment[]) {
  return rows.filter((r) => waveWillEmail(r.Status))
}

export function sendableCount(rows: TenantUserActivationFragment[]) {
  return sendableRows(rows).length
}

/** Work-queue order for the status sort: actionable states first. */
const STATUS_SORT_RANK: Record<string, number> = {
  EXPIRED: 1,
  PENDING: 2,
  NOT_MIGRATED: 3,
  MIGRATED_ELSEWHERE: 4,
  ACTIVATED: 5,
  BLOCKED: 6,
}

function memberName(row: TenantUserActivationFragment) {
  return (
    `${row.FirstName ?? ""} ${row.LastName ?? ""}`.trim() || row.Email || ""
  )
}

/** One sort applied to every team's table, so scanning stays consistent. */
export function sortRows(
  rows: TenantUserActivationFragment[],
  sort: MigrationCenterMemberSort,
): TenantUserActivationFragment[] {
  const dir = sort.desc ? -1 : 1
  return [...rows].sort((a, b) => {
    switch (sort.key) {
      case "app":
        return (Number(b.UsesV2) - Number(a.UsesV2)) * dir
      case "sent": {
        const aT = a.ActivationLastSentAt ?? ""
        const bT = b.ActivationLastSentAt ?? ""
        return aT.localeCompare(bT) * dir
      }
      case "status":
        return (
          ((STATUS_SORT_RANK[a.Status] ?? 9) -
            (STATUS_SORT_RANK[b.Status] ?? 9)) *
          dir
        )
      default:
        return memberName(a).localeCompare(memberName(b), "nb") * dir
    }
  })
}
